import React from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { ApiCredentials, CopiedType } from '../types';

interface CredentialsDisplayProps {
  credentials: ApiCredentials;
  copiedItem: CopiedType;
  onCopy: (text: string, type: CopiedType) => void;
  onCopyBoth: () => void;
  savedConfirmed: boolean;
  onSavedConfirmedChange: (confirmed: boolean) => void;
}

/**
 * Displays API credentials with copy functionality after successful creation.
 * Shows the key and secret (which can only be viewed once) with copy buttons.
 */
const CredentialsDisplay: React.FC<CredentialsDisplayProps> = ({
  credentials,
  copiedItem,
  onCopy,
  onCopyBoth,
  savedConfirmed,
  onSavedConfirmedChange,
}) => {
  const keyCopied = copiedItem === 'key';
  const secretCopied = copiedItem === 'secret';
  const bothCopied = copiedItem === 'both';

  return (
    <Box>
      {/* Success Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <CheckIcon sx={{ color: 'success.main', fontSize: 28, mr: 1 }} />
        <Typography variant="h6" sx={{ color: 'success.dark', fontWeight: 600 }}>
          Credentials Created
        </Typography>
      </Box>

      {/* API Key Card */}
      <Paper 
        variant="outlined" 
        sx={{ 
          p: 2, 
          mb: 2, 
          bgcolor: 'grey.50',
          borderColor: 'grey.300'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            API Key
          </Typography>
          <Button
            size="small"
            startIcon={keyCopied ? <CheckIcon /> : <ContentCopyIcon />}
            onClick={() => onCopy(credentials.key || '', 'key')}
            color={keyCopied ? "success" : "inherit"}
            sx={{ textTransform: 'none', minWidth: 'auto' }}
          >
            {keyCopied ? 'Copied' : 'Copy'}
          </Button>
        </Box>
        <Typography 
          sx={{ 
            fontFamily: 'monospace', 
            fontSize: '0.875rem',
            wordBreak: 'break-all',
            color: 'text.primary'
          }}
        >
          {credentials.key ?? ''}
        </Typography>
      </Paper>

      {/* Secret Card - Emphasized */}
      <Paper 
        variant="outlined" 
        sx={{ 
          p: 2, 
          mb: 2, 
          bgcolor: 'rgba(237, 108, 2, 0.08)',
          borderColor: 'warning.main',
          borderWidth: 2
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'warning.dark', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Secret
            </Typography>
            <Typography variant="caption" sx={{ color: 'warning.dark' }}>
              (shown once)
            </Typography>
          </Box>
          <Button
            size="small"
            startIcon={secretCopied ? <CheckIcon /> : <ContentCopyIcon />}
            onClick={() => onCopy(credentials.secret || '', 'secret')}
            color={secretCopied ? "success" : "inherit"}
            sx={{ textTransform: 'none', minWidth: 'auto' }}
          >
            {secretCopied ? 'Copied' : 'Copy'}
          </Button>
        </Box>
        <Typography 
          sx={{ 
            fontFamily: 'monospace', 
            fontSize: '0.875rem',
            wordBreak: 'break-all',
            color: 'text.primary'
          }}
        >
          {credentials.secret ?? ''}
        </Typography>
      </Paper>

      {/* Copy Both Button */}
      <Button
        variant="outlined"
        fullWidth
        startIcon={bothCopied ? <CheckIcon /> : <ContentCopyIcon />}
        onClick={onCopyBoth}
        color={bothCopied ? "success" : "primary"}
        sx={{ mb: 3, textTransform: 'none', py: 1 }}
      >
        {bothCopied ? 'Both Copied' : 'Copy Both to Clipboard'}
      </Button>

      {/* Confirmation Checkbox */}
      <FormControlLabel
        control={
          <Checkbox
            checked={savedConfirmed}
            onChange={(e) => onSavedConfirmedChange(e.target.checked)}
            color="primary"
          />
        }
        label={
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            I have saved these credentials securely
          </Typography>
        }
        sx={{ mt: 1 }}
      />
    </Box>
  );
};

export default CredentialsDisplay;
